import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

export default function MyReports() {
    const { authFetch } = useAuth();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchReports();
    }, []);

    const fetchReports = async () => {
        try {
            const res = await authFetch('/api/reports');
            if (res.ok) {
                const data = await res.json();
                setReports(data.reports || []);
            }
        } catch (err) {
            setError('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const download = async (report) => {
        setDownloadingId(report.id);
        try {
            const res = await authFetch(`/api/reports/${report.id}/download`);
            if (!res.ok) throw new Error('Download failed');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = report.filename;
            a.click();
            URL.revokeObjectURL(url);
            fetchReports();
        } catch (err) {
            setError('Download failed: ' + err.message);
        } finally {
            setDownloadingId(null);
        }
    };

    if (loading) return <LoadingSpinner fullPage text="Loading your reports..." />;

    return (
        <div className="max-w-5xl mx-auto px-4 py-8">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-extrabold" style={{ color: '#1a1a2e' }}>My Reports</h1>
                    <p className="text-gray-500 text-sm mt-0.5">{reports.length} report{reports.length !== 1 ? 's' : ''} in your library</p>
                </div>
                <Link to="/dashboard" className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors">
                    <i className="fas fa-arrow-left text-xs"></i> Dashboard
                </Link>
            </div>

            {error && (
                <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    <i className="fas fa-exclamation-circle mr-2"></i>{error}
                </div>
            )}

            {reports.length === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5" style={{ background: 'rgba(233,69,96,0.1)' }}>
                        <i className="fas fa-file-alt text-3xl" style={{ color: '#e94560' }}></i>
                    </div>
                    <h3 className="font-bold text-xl text-gray-900 mb-2">No reports yet</h3>
                    <p className="text-gray-500 mb-6">Generate your first arbitrage report from the dashboard or purchase a plan to get started.</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/dashboard" className="px-6 py-3 rounded-xl font-bold text-white text-sm" style={{ background: '#e94560' }}>
                            Go to Dashboard
                        </Link>
                        <Link to="/pricing" className="px-6 py-3 rounded-xl font-bold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50">
                            View Pricing Plans
                        </Link>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="font-bold text-gray-900">All Reports</h3>
                        <span className="text-xs text-gray-400">{reports.length} total</span>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {reports.map((report, i) => (
                            <div key={report.id} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors">
                                <div className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ background: 'rgba(233,69,96,0.1)' }}>
                                    <i className="fas fa-file-pdf" style={{ color: '#e94560' }}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-gray-900 text-sm">{report.category} — {report.product_type}</div>
                                    <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-3">
                                        <span><i className="fas fa-calendar mr-1"></i>{new Date(report.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                                        <span><i className="fas fa-download mr-1"></i>{report.download_count} download{report.download_count !== 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                                <div className="flex-shrink-0">
                                    <button onClick={() => download(report)} disabled={downloadingId === report.id}
                                        className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm text-white transition-all hover:opacity-90 disabled:opacity-60"
                                        style={{ background: downloadingId === report.id ? '#666' : '#1a1a2e' }}>
                                        {downloadingId === report.id ? (
                                            <><span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>Downloading</>
                                        ) : (
                                            <><i className="fas fa-download text-xs"></i>Download PDF</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
